import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {serverTableControlIconSx, serverTableControlSx} from "../constants";
import {
    useServerTableToolbarContext,
    useServerTableToolbarItemContext
} from "../ServerTableToolbarContext";
import {defineServerTableToolbarItem} from "../ServerTableToolbarItem";
import {ServerTableExportDialog} from "../export/ServerTableExportDialog";
import {type ServerTableExportOptions, useServerTableExport} from "../export/useServerTableExport";

export type ServerTableExportProps<TRequest> = ServerTableExportOptions<TRequest>;

/**
 * The toolbar's Export control, next to Print: CSV or Excel of the list as the API renders it (the live search,
 * filters and sort). It opens `ServerTableExportDialog` for the format, the columns and the row count, and spins while
 * the file is made. The toolbar face of `useServerTableExport`; `ServerTableRailExport` is the rail's. The API must
 * know the list (crqlar-api-v3 `service/export/list/`); read-only users cannot export.
 */
function ServerTableExportComponent<TRequest>(props: ServerTableExportProps<TRequest>) {
    const {t} = useTranslation();
    const {disabled, running, columns, visibleColumnIds, pageRowCount, pageOffset, totalRowCount, run} = useServerTableExport(props);
    const {closePanel} = useServerTableToolbarContext();
    const toolbarItem = useServerTableToolbarItemContext();
    const [open, setOpen] = useState(false);
    const unavailable = disabled || Boolean(running);

    const openDialog = () => {
        closePanel();
        toolbarItem.closeMenu();
        setOpen(true);
    };

    return (
        <>
            {toolbarItem.presentation === 'menu' ? (
                <MenuItem disabled={unavailable} onClick={openDialog}>
                    {running
                        ? <CircularProgress size={18} sx={{mr: 1}}/>
                        : <FileDownloadOutlinedIcon fontSize="small" sx={{mr: 1, color: 'primary.main'}}/>}
                    {t('Export')}
                </MenuItem>
            ) : (
                <Tooltip title={t('Export')}>
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            data-dashboard-control=""
                            data-active={open || undefined}
                            aria-label={t('Export')}
                            aria-haspopup="dialog"
                            aria-busy={Boolean(running)}
                            disabled={unavailable}
                            onClick={openDialog}
                            sx={serverTableControlSx}
                        >
                            {running
                                ? <CircularProgress size={18} color="inherit"/>
                                : <FileDownloadOutlinedIcon sx={serverTableControlIconSx}/>}
                        </IconButton>
                    </span>
                </Tooltip>
            )}

            <ServerTableExportDialog
                open={open}
                onClose={() => setOpen(false)}
                title={props.title}
                columns={columns}
                visibleColumnIds={visibleColumnIds}
                pageRowCount={pageRowCount}
                pageOffset={pageOffset}
                totalRowCount={totalRowCount}
                onExport={(format, options) => void run(format, options)}
            />
        </>
    );
}

export const ServerTableExport = defineServerTableToolbarItem(ServerTableExportComponent);
